import { Provider } from "react-redux";
import { useRef, useEffect, useState } from "react";
import { restoreSession } from "../utils/request";
import logoutAndRedirect from "../utils/logoutAndRedirect";
import { getAccessToken } from "../utils/tokenStore";
import type { AppProps } from "next/app";
import { store } from "../app/store";
import { getCookie, setCookie, clearAllCookies } from "../utils/cookie";
import { identifySentryUser, clearSentryUser } from "../utils/sentryUser";
import clearLocalSession, {
  isImpersonatingSession,
  revokeRefreshCookie,
} from "../utils/clearLocalSession";
import router from "next/router";
import { logger } from "../utils/logger";

import "../styles/globals.scss";
import Head from "next/head";
import PWAInstallPrompt from "../components/Common/PWAInstallPrompt";

function MyApp({ Component, pageProps }: AppProps) {
  const hasRedirected = useRef(false);

  // sellsmart-api#4626: the access token lives in memory, so a page load starts
  // without one. Restore it from the HttpOnly refresh cookie (or migrate a stored
  // token) before the page renders and fires its requests.
  const [sessionRestored, setSessionRestored] = useState(false);

  useEffect(() => {
    restoreSession().then((result) => {
      // The cookie can no longer refresh the session (logged out elsewhere,
      // expired, revoked): sign out locally. Already on /sign-in (for example
      // with an impersonation link), stay there so the link's query survives.
      if (result === "refused") {
        const onSignIn = router.pathname === "/sign-in";
        void logoutAndRedirect("/sign-in", { navigate: !onSignIn }).finally(
          () => setSessionRestored(true),
        );
        return;
      }
      setSessionRestored(true);
    });
  }, []);

  // Register service worker for PWA (manual, no next-pwa)
  useEffect(() => {
    logger.pwa.log("NODE_ENV:", process.env.NODE_ENV);

    if (process.env.NODE_ENV !== "production") return;
    if (typeof window !== "undefined" && "serviceWorker" in navigator) {
      logger.pwa.log("Service Worker support detected");

      const registerServiceWorker = async () => {
        try {
          logger.pwa.log("Starting service worker registration...");

          const registration = await navigator.serviceWorker.register(
            "/sw.js",
            {
              scope: "/",
            },
          );

          logger.pwa.log("✅ Service Worker registered successfully!", {
            scope: registration.scope,
            updateViaCache: registration.updateViaCache,
          });

          // Listen for updates
          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;
            logger.pwa.log(
              "Service Worker update found, installing new version...",
            );

            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                logger.pwa.log(
                  "Service Worker state changed:",
                  newWorker.state,
                );
                if (newWorker.state === "activated") {
                  logger.pwa.log("✅ New Service Worker activated!");
                }
              });
            }
          });

          // Check for existing service worker
          if (registration.active) {
            logger.pwa.log(
              "Active service worker found:",
              registration.active.scriptURL,
            );
          }
          if (registration.waiting) {
            logger.pwa.log("Waiting service worker found (update available)");
          }
          if (registration.installing) {
            logger.pwa.log("Installing service worker...");
          }
        } catch (err) {
          logger.pwa.error("❌ Service Worker registration failed:", err);
        }
      };

      // Handle PWA install prompt
      // Initialize window.deferredPrompt to null
      window.deferredPrompt = null;

      const handleBeforeInstallPrompt = (e: Event) => {
        logger.pwa.log("🎯 Install prompt available!");
        // Prevent the mini-infobar from appearing on mobile
        e.preventDefault();
        // Stash the event on window so it can be accessed by other pages
        window.deferredPrompt = e as BeforeInstallPromptEvent;
        logger.pwa.log(
          "Install prompt stored. Call window.promptPWAInstall() to show it.",
        );

        // Make install function available globally for testing
        window.promptPWAInstall = async () => {
          const deferredPrompt = window.deferredPrompt;
          if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            logger.pwa.log(`User response to install prompt: ${outcome}`);
            window.deferredPrompt = null;
          } else {
            logger.pwa.log("No install prompt available");
          }
        };
      };

      const handleAppInstalled = () => {
        logger.pwa.log("✅ App installed successfully!");
        window.deferredPrompt = null;
      };

      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.addEventListener("appinstalled", handleAppInstalled);

      // Add PWA debugging utility
      (window as any).checkPWAStatus = async () => {
        logger.info("=== PWA Status Check ===");

        // Check service worker
        if ("serviceWorker" in navigator) {
          const registration = await navigator.serviceWorker.getRegistration();
          logger.info("Service Worker registered:", !!registration);
          if (registration) {
            logger.info("  Scope:", registration.scope);
            logger.info("  Active:", !!registration.active);
            logger.info("  Waiting:", !!registration.waiting);
            logger.info("  Installing:", !!registration.installing);
            if (registration.active) {
              logger.info("  Active SW URL:", registration.active.scriptURL);
              logger.info("  Active SW State:", registration.active.state);
            }
          }
        }

        // Check manifest
        const manifestLink = document.querySelector('link[rel="manifest"]');
        logger.info("Manifest link found:", !!manifestLink);
        if (manifestLink) {
          logger.info("  Manifest href:", manifestLink.getAttribute("href"));
        }

        // Check if installed
        if (window.matchMedia("(display-mode: standalone)").matches) {
          logger.info("App is running in standalone mode (installed)");
        } else {
          logger.info("App is running in browser mode (not installed)");
        }

        // Check install prompt
        logger.info("Install prompt available:", !!window.deferredPrompt);

        logger.info(
          "\nTo trigger install prompt (if available), run: window.promptPWAInstall()",
        );
        logger.info("========================");
      };

      logger.pwa.log("Debug utility available: window.checkPWAStatus()");

      const handleWindowLoad = () => {
        logger.pwa.log("Window loaded, registering service worker...");
        void registerServiceWorker();
      };

      if (document.readyState === "complete") {
        handleWindowLoad();
      } else {
        window.addEventListener("load", handleWindowLoad);
      }

      return () => {
        window.removeEventListener("load", handleWindowLoad);
        window.removeEventListener(
          "beforeinstallprompt",
          handleBeforeInstallPrompt,
        );
        window.removeEventListener("appinstalled", handleAppInstalled);
        // Clean up window reference
        window.deferredPrompt = null;
      };
    } else {
      logger.pwa.log("Service Worker not supported in this browser");
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (pageProps?.auth && !hasRedirected.current) {
      if (!getAccessToken() && !getCookie("authenticated")) {
        hasRedirected.current = true;
        // This session-expiry path bypasses logoutAndRedirect, so it runs
        // that cleanup itself. clearLocalSession() first, with the
        // impersonation flag read before the cookies go (#4334 S6.3). Then
        // the Sentry context — otherwise the expired user (and any
        // `impersonated` tag) stays on the scope for the rest of the SPA
        // session and later errors are misattributed to them.
        // sellsmart-api#4626: also end the refresh cookie's session, or the next
        // page load would sign the user straight back in.
        void revokeRefreshCookie();
        void clearLocalSession({ impersonating: isImpersonatingSession() });
        clearAllCookies();
        clearSentryUser();
        setCookie("inaccessibleurl", router.pathname);
        router.replace("/sign-in");
      }
    }
  }, [pageProps?.auth]);

  // Attach the logged-in user's id/email to Sentry so error events include
  // who was affected. Covers an already-authenticated session on page
  // load/refresh — a fresh login/impersonation identifies immediately via
  // their own API thunks (see api/auth/loginApi.tsx, api/impersonate/getSupplierData.ts).
  // With no "auth" cookie we clear rather than leave the scope untouched, so
  // the Sentry context always matches the cookie state at mount.
  useEffect(() => {
    if (getCookie("auth")) {
      identifySentryUser();
    } else {
      clearSentryUser();
    }
  }, []);

  // Guard against undefined Component (can occur during SSR with Sentry instrumentation).
  // It sits after the last hook so every render calls the same hooks in the same order.
  if (!Component) {
    return null;
  }

  return (
    <Provider store={store}>
      <Head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700&family=Poppins:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <meta name="theme-color" content="#0C294A" />
        <meta name="application-name" content="SellSmart Supplier Portal" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="SellSmart Supplier" />
        <link rel="manifest" href="/manifest.json" />
        <link
          rel="apple-touch-icon"
          href="/pwa/sellsmart-logomark-180x180-white.png"
        />
      </Head>
      <PWAInstallPrompt />
      {sessionRestored ? <Component {...pageProps} /> : null}
    </Provider>
  );
}

export default MyApp;
